import { AnyAction } from 'redux'
import { ProjectApiCreateActivityBody } from '@/app/models/projects-service'
import { NewActivityExecutionActions } from '../actions/activity-execution'
import { AssigneeProps } from '@/app/models/assignee'
import { PhotoGallery } from '@/app/models/photo'

export interface ExecutionMessageAuthor {
    name: string
    lastname: string
    avatar?: string // base64 ?
    nickname?: string
}

export interface ExecutionMessageReaction {
    total: number
    userReaction?: string
    typeList: string[]
}

export interface ExecutionMessageComment {
    id: number
    author: ExecutionMessageAuthor
    comment: string
    creationDate: string
    replyList?: ExecutionMessageComment[]
    reactions?: ExecutionMessageReaction
}

export interface ExecutionMessage {
    id: string
    author: ExecutionMessageAuthor
    reason: string
    stores: any[]
    message: string
    attachmentList: any[]
    isUnread?: boolean
    reactions?: ExecutionMessageReaction
    commentsList?: ExecutionMessageComment[]
    creationDate?: string
    isEdit?: boolean
    totalReply?: number
}

export interface NewActivityExecutionState {
    messageList: ExecutionMessage[]
    totalMessage: number
    messagePageNum: number
    isApproval: boolean
    isMultiStore: boolean
    isFullScreen: boolean
    storeId: string
    projectDetails: ProjectApiCreateActivityBody
    loadingProjectDetails: boolean
    assignees: AssigneeProps[]
    loadingAssignees: boolean
    isMessageListLoading: boolean
    isMessageCreateLoading: boolean
    isMessageReactionLoading: boolean
    storesList: any[]
    loadingSurveyStatus: boolean
    surveyStatusUpdateMap: any
    activityPhotoList: PhotoGallery[]
    totalActivityPhotoList: number
    loadingActivityPhotoList: boolean
    isPhotographicApprovalAccept: boolean
}

export const NEW_ACTIVITY_EXECUTION_NAMESPACE = 'newActivityExecutionReducer'

const defaultState: NewActivityExecutionState = {
    messageList: [],
    totalMessage: 0,
    messagePageNum: 1,
    isMessageListLoading: false,
    isMessageCreateLoading: false,
    isMessageReactionLoading: false,
    isApproval: false,
    isMultiStore: false,
    isFullScreen: false,
    storeId: null,
    projectDetails: {
        _id: '',
        projectTitle: '',
        department: '',
        assignment: [],
        creatorUserInfo: {
            name: '',
            lastname: '',
            role: '',
            userId: '',
            date: 0,
        },
        enableMessaging: false,
        enableCommentsAndReactions: false,
        priority: '',
        status: null,
        projectType: null,
        module: '',
        type: '',
        requiredConfirmReading: false,
        information: '',
        questions: [],
        jobs: [],
        storesIdList: [],
        storesList: [],
        startDate: 0,
        endDate: 0,
        visibility: null,
        visibilityDate: 0,
        autoclose: false,
        autocloseDate: null,
        visibilityCalendar: false,
        surveys: [],
        timeZone: '',
    },
    loadingProjectDetails: true,
    assignees: [],
    loadingAssignees: true,
    loadingSurveyStatus: false,
    surveyStatusUpdateMap: {},
    storesList: [],
    activityPhotoList: [],
    totalActivityPhotoList: 0,
    loadingActivityPhotoList: false,
    isPhotographicApprovalAccept: false
}

export default (state: NewActivityExecutionState = defaultState, action: AnyAction) => {
    let newMessageList, newMessage
    switch (action.type) {
        case NewActivityExecutionActions.SET_MESSAGE_LIST:
            return {
                ...state,
                messageList: [...action.messages],
            }
        case NewActivityExecutionActions.SET_TOTAL_MESSAGE:
            return {
                ...state,
                totalMessage: action.totalMessage,
            }
        case NewActivityExecutionActions.SET_MESSAGE_PAGE_NUM:
            return {
                ...state,
                messagePageNum: action.messagePageNum,
            }
        case NewActivityExecutionActions.SET_MESSAGE_LIST_LOADING:
            return {
                ...state,
                isMessageListLoading: action.loading,
            }
        case NewActivityExecutionActions.SET_MESSAGE_CREATE_LOADING:
            return {
                ...state,
                isMessageCreateLoading: action.loading,
            }
        case NewActivityExecutionActions.SET_MESSAGE_REACTION_LOADING:
            return {
                ...state,
                isMessageReactionLoading: action.loading,
            }
        case NewActivityExecutionActions.UPDATE_MESSAGE_LIST:
            newMessageList = [...state.messageList]
            newMessage = {
                ...newMessageList[
                    action.payload.index > -1 ? action.payload.index : newMessageList.length
                ],
                ...action.payload.message,
            }
            if (action.payload.index > -1) {
                newMessageList[action.payload.index] = {
                    ...newMessage,
                }
            } else {
                newMessageList = [newMessage, ...newMessageList]
            }
            return {
                ...state,
                messageList: [...newMessageList],
            }
        case NewActivityExecutionActions.REMOVE_MESSAGE:
            newMessageList = [...state.messageList]
            newMessageList.splice(action.index, 1)
            return {
                ...state,
                messageList: [...newMessageList],
            }
        case NewActivityExecutionActions.SET_IS_APPROVAL:
            return {
                ...state,
                isApproval: action.value,
            }
        case NewActivityExecutionActions.SET_IS_MULTISTORE:
            return {
                ...state,
                isMultiStore: action.value,
            }
        case NewActivityExecutionActions.SET_IS_FULLSCREEN:
            return {
                ...state,
                isFullScreen: action.value,
            }
        case NewActivityExecutionActions.SET_STORE_ID:
            return {
                ...state,
                storeId: action.storeId,
            }
        case NewActivityExecutionActions.GET_PROJECT_DETAILS_REQUEST:
            return {
                ...state,
                loadingProjectDetails: true,
            }
        case NewActivityExecutionActions.SET_PROJECT_DETAILS:
            return {
                ...state,
                projectDetails: action.projectDetails,
            }
        case NewActivityExecutionActions.GET_PROJECT_DETAILS_SUCCESS:
            return {
                ...state,
                projectDetails: action.projectDetails,
                storesList: action.projectDetails.storesList,
                loadingProjectDetails: false,
            }
        case NewActivityExecutionActions.GET_PROJECT_DETAILS_FAILURE:
            return {
                ...state,
                loadingProjectDetails: false,
            }
        case NewActivityExecutionActions.GET_ASSIGNEES_REQUEST:
            return {
                ...state,
                loadingAssignees: true,
            }
        case NewActivityExecutionActions.GET_ASSIGNEES_SUCCESS:
            return {
                ...state,
                assignees: action.assignees,
                loadingAssignees: false,
            }
        case NewActivityExecutionActions.GET_ASSIGNEES_FAILURE:
            return {
                ...state,
                loadingAssignees: false,
            }
        case NewActivityExecutionActions.GET_SURVEY_STATUS_REQUEST:
            return {
                ...state,
                loadingSurveyStatus: true,
            }
        case NewActivityExecutionActions.GET_SURVEY_STATUS_SUCCESS:
            return {
                ...state,
                loadingSurveyStatus: false,
                surveyStatusUpdateMap: action.success,
            }
        case NewActivityExecutionActions.GET_SURVEY_STATUS_ERROR:
            return {
                ...state,
                loadingSurveyStatus: false,
            }
        case NewActivityExecutionActions.RESET_ACTIVITY_TASK_PHOTO:
            return {
                ...state,
                activityPhotoList: [],
            }
        case NewActivityExecutionActions.GET_ACTIVITY_TASK_PHOTO_REQUEST:
            return {
                ...state,
                loadingActivityPhotoList: true,
                // activityPhotoList: [],
            }
        case NewActivityExecutionActions.GET_ACTIVITY_TASK_PHOTO_SUCCESS:
            return {
                ...state,
                loadingActivityPhotoList: false,
                activityPhotoList: [...state.activityPhotoList, ...action.success.data],
                totalActivityPhotoList: action.success.totalRecords,
            }
        case NewActivityExecutionActions.GET_ACTIVITY_TASK_PHOTO_ERROR:
            return {
                ...state,
                loadingActivityPhotoList: false,
            }
        case NewActivityExecutionActions.SET_IS_PHOTOGRAPHIC_APPROVAL_ACCEPT:
            return {
                ...state,
                isPhotographicApprovalAccept: action.value,
            }
        default:
            return {
                ...state,
            }
    }
}
